'use client';

import { useEffect, useState } from 'react';
import { useBatchEvaluateMutation } from '@cntxt/data-access/access-control';
import type { BooleanFlagKey, FeatureFlagKey, VariantFlagKey } from '../registry/feature-flags';
import type { BooleanFlagResult, BooleanFlagResultBase, FeatureFlagOptions, GenericFlagResultBase, MultipleFlagsResultBase, UnifiedFlagResult, VariantFlagResult, VariantFlagResultBase } from '../types/shared';
import { deduplicateFlagKeys } from '../utils/deduplicate-flags';
import { extractVariantInfo, processMultipleFlagResults } from '../utils/process-flag-results';
import type { ValidateNoDuplicates } from '../utils/type-helpers';
import type { FlagEvaluationResult } from '../utils/types';

/**
 * Client-side specific properties for async operations
 */
type ClientAsyncProps = {
  /** Loading state for async flag evaluation */
  isLoading: boolean;
  /** Error if evaluation failed */
  error: Error | null;
  /** Function to manually re-evaluate the flag(s) */
  refetch: () => void;
};

/**
 * Client-side specific properties for result data.
 * Generic over the flag result type for proper narrowing.
 */
type ClientResultProps<T extends UnifiedFlagResult = UnifiedFlagResult> = {
  /** Full evaluation result with metadata */
  result: T | null;
};

/**
 * Options for useFeatureFlag hook
 * Extends base options with client-specific enabled flag
 */
export type UseFeatureFlagOptions = FeatureFlagOptions & {
  /** Whether the hook is enabled (default: true) */
  enabled?: boolean;
};

/**
 * Boolean flag result for client-side (React hooks)
 * Extends base boolean result with client-specific async properties.
 * The `result` property is typed as `BooleanFlagResult | null`.
 */
export type UseBooleanFlagResult = BooleanFlagResultBase & ClientResultProps<BooleanFlagResult> & ClientAsyncProps;

/**
 * Variant flag result for client-side (React hooks)
 * Extends base variant result with client-specific async properties.
 * The `result` property is typed as `VariantFlagResult | null`.
 */
export type UseVariantFlagResult = VariantFlagResultBase & ClientResultProps<VariantFlagResult> & ClientAsyncProps;

/**
 * Generic flag result for client-side (React hooks)
 * Extends base generic result with client-specific async properties.
 * The `result` property is typed as `UnifiedFlagResult | null` (union of both).
 */
export type UseFeatureFlagResult = GenericFlagResultBase & ClientResultProps & ClientAsyncProps;

/**
 * Multiple flags result for client-side (React hooks)
 * Extends base multiple flags result with client-specific async properties
 */
export type UseFeatureFlagsResult = MultipleFlagsResultBase & ClientAsyncProps;

/**
 * React hook to evaluate a boolean feature flag (type-safe)
 *
 * @param flagKey Boolean feature flag key from registry
 * @param options Hook options including default value
 * @returns Object with enabled state, loading state, error, and refetch function
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { enabled, isLoading } = useFeatureFlag('new-dashboard');
 *   if (isLoading) return <Spinner />;
 *   return enabled ? <NewDashboard /> : <OldDashboard />;
 * }
 * ```
 */
export function useFeatureFlag<K extends BooleanFlagKey>(flagKey: K, options?: UseFeatureFlagOptions): UseBooleanFlagResult;

/**
 * React hook to evaluate a variant feature flag (type-safe)
 *
 * @param flagKey Variant feature flag key from registry
 * @param options Hook options
 * @returns Object with variant data, loading state, error, and refetch function
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { variantKey, match, isLoading } = useFeatureFlag('homepage-layout');
 *   if (isLoading) return <Spinner />;
 *   if (variantKey === 'modern') return <ModernLayout />;
 *   return <ClassicLayout />;
 * }
 * ```
 */
export function useFeatureFlag<K extends VariantFlagKey>(flagKey: K, options?: UseFeatureFlagOptions): UseVariantFlagResult;

/**
 * React hook to evaluate a feature flag when the exact type (boolean vs variant)
 * is not known at compile time. Still restricted to registered flag keys.
 *
 * @param flagKey Feature flag key from registry
 * @param options Hook options
 * @returns Generic result that supports both boolean and variant flags
 */
export function useFeatureFlag(flagKey: FeatureFlagKey, options?: UseFeatureFlagOptions): UseFeatureFlagResult;

/**
 * React hook to evaluate multiple feature flags in a single request (type-safe)
 * More efficient than using multiple useFeatureFlag hooks
 *
 * @param flagKeys Array of feature flag keys from registry (duplicates will cause a type error)
 * @param options Hook options including default value
 * @returns Object with flags map, full results, loading state, error, and refetch function
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { flags, results, isLoading } = useFeatureFlag([
 *     'new-dashboard',
 *     'announcement-rail'
 *   ] as const); // Use 'as const' to enable duplicate detection
 *
 *   if (isLoading) return <Spinner />;
 *
 *   return (
 *     <div>
 *       {flags['new-dashboard'] && <NewDashboard />}
 *       {results['announcement-rail']?.variantKey === 'modern' && <ModernLayout />}
 *     </div>
 *   );
 * }
 * ```
 */
export function useFeatureFlag<T extends readonly FeatureFlagKey[] = readonly FeatureFlagKey[]>(flagKeys: ValidateNoDuplicates<T>, options?: UseFeatureFlagOptions): UseFeatureFlagsResult;
export function useFeatureFlag(flagKeyOrKeys: FeatureFlagKey | readonly FeatureFlagKey[], options: UseFeatureFlagOptions = {}): UseBooleanFlagResult | UseVariantFlagResult | UseFeatureFlagResult | UseFeatureFlagsResult {
  const {
    defaultValue = false,
    enabled: hookEnabled = true
  } = options;
  const isSingleFlag = typeof flagKeyOrKeys === 'string';
  // Deduplicate flag keys to prevent redundant evaluations
  const flagKeys = isSingleFlag ? [flagKeyOrKeys] : deduplicateFlagKeys(Array.from(flagKeyOrKeys));
  const [flags, setFlags] = useState<Partial<Record<FeatureFlagKey, boolean>>>(() => flagKeys.reduce((acc, key) => {
    acc[key] = defaultValue;
    return acc;
  }, {} as Partial<Record<FeatureFlagKey, boolean>>));
  const [results, setResults] = useState<Partial<Record<FeatureFlagKey, UnifiedFlagResult>>>({});
  const [error, setError] = useState<Error | null>(null);
  const mutation = useBatchEvaluateMutation();
  const evaluate = (): void => {
    if (!hookEnabled || flagKeys.length === 0) {
      return;
    }
    mutation.mutate({
      flags: flagKeys,
      defaultValue
    }, {
      onSuccess: response => {
        // The response is wrapped in { data: BatchEvaluationResponse }
        // Extract the actual data: Record<string, FlagEvaluationResult>
        const data = (response as {
          data?: Record<string, FlagEvaluationResult>;
        }).data || response as Record<string, FlagEvaluationResult>;

        // Use shared utility to process the results
        const {
          flags: newFlags,
          results: newResults
        } = processMultipleFlagResults(data, defaultValue);
        setFlags(newFlags);
        setResults(newResults);
        setError(null);
      },
      onError: err => {
        // Keep default values on error
        setError(err instanceof Error ? err : new Error('Failed to evaluate flags'));
      }
    });
  };
  useEffect(() => {
    evaluate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(flagKeys), defaultValue, hookEnabled]);

  // Return shape based on input type
  if (isSingleFlag) {
    const flagKey = flagKeyOrKeys;
    const result = results[flagKey] ?? null;

    // Use shared utility to extract variant info
    const variantInfo = extractVariantInfo(result);
    return {
      enabled: flags[flagKey] ?? defaultValue,
      ...variantInfo,
      result,
      isLoading: mutation.isPending,
      error,
      refetch: evaluate
    };
  }
  return {
    flags,
    results,
    isLoading: mutation.isPending,
    error,
    refetch: evaluate
  };
}